import { formatHeatmapSlotRange, HeatmapGridCell } from '@/components/HeatmapGridCell';
import { cn } from '@/lib/utils';
import { CONTENT_HOUR_SLOTS, slotCountsFromHours } from '../lib/contentHourBuckets';
import { contentTypeMeta, contentTypeShortLabel } from '../lib/engagementColors';
import { EngagementTimezoneFilter } from './EngagementHeatmapCard';

export type ContentTypeHourSlice = {
  type: string;
  label: string;
  hourCounts?: number[];
};

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const n = Number.parseInt(raw, 16);
  if (!Number.isFinite(n)) return `rgba(255, 255, 255, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function slotColor(hex: string, value: number, max: number): string {
  if (max <= 0 || value <= 0) return 'rgba(255, 255, 255, 0.06)';
  const t = Math.min(1, value / max);
  return hexToRgba(hex, 0.18 + t * 0.82);
}

/**
 * Mini heatmap under a Figma card: timezone on the left, 2-hour slots on Y,
 * content types on X. Intensity is scaled per type.
 */
export function ContentTypeHourHeatmap({
  slices,
  title,
  valueNoun,
  timezone,
  onTimezoneChange,
  ariaLabel,
  className,
}: {
  slices: ContentTypeHourSlice[];
  title: string;
  valueNoun: string;
  timezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  ariaLabel?: string;
  className?: string;
}) {
  const columns = slices.map((slice) => {
    const slots = slotCountsFromHours(slice.hourCounts);
    return {
      slice,
      slots,
      peak: Math.max(0, ...slots),
      color: contentTypeMeta(slice.type).color,
      short: contentTypeShortLabel(slice.type, slice.label),
    };
  });

  const showTimezone = timezone != null && onTimezoneChange != null;

  return (
    <div
      className={cn(
        'border-t border-white/5 pt-4',
        showTimezone
          ? 'flex flex-col gap-4 sm:flex-row sm:items-start'
          : 'space-y-2',
        className,
      )}
    >
      {showTimezone ? (
        <EngagementTimezoneFilter
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
          ariaLabel={ariaLabel ?? 'Change heatmap timezone'}
          className="w-full shrink-0 sm:w-[240px]"
        />
      ) : null}
      <div className={cn('space-y-2', showTimezone && 'min-w-0 flex-1')}>
        <p className="text-[11px] font-medium text-muted-foreground">{title}</p>
        <div
          className="grid gap-x-1 gap-y-[3px]"
          style={{
            gridTemplateColumns: `2.75rem repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))`,
          }}
        >
          {CONTENT_HOUR_SLOTS.map((slot, slotIndex) => (
            <div key={slot.startHour} className="contents">
              <div className="flex h-5 items-center justify-end pr-1.5 text-[10px] tabular-nums text-muted-foreground">
                {slot.label}
              </div>
              {columns.map((column) => {
                const value = column.slots[slotIndex] ?? 0;
                const shareOfPeak =
                  column.peak > 0 && value > 0
                    ? `${Math.round((value / column.peak) * 100)}% of this type's peak`
                    : undefined;
                return (
                  <HeatmapGridCell
                    key={`${column.slice.type}-${slot.startHour}`}
                    dayLabel={column.slice.label}
                    timeLabel={formatHeatmapSlotRange(slot.startHour, timezone)}
                    value={value}
                    valueNoun={valueNoun}
                    {...(shareOfPeak != null ? { detail: shareOfPeak } : {})}
                    className="h-5 rounded-sm"
                    style={{ backgroundColor: slotColor(column.color, value, column.peak) }}
                  />
                );
              })}
            </div>
          ))}
          <div />
          {columns.map((column) => (
            <div
              key={column.slice.type}
              className="truncate pt-1 text-center text-[10px] text-muted-foreground"
              title={column.slice.label}
            >
              {column.short}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
